// Psychotherapy details (psychotherapy variant of the onboarding).
//
// Statutory insured patients (insurance type "public") answer what the insurer
// application needs: KVNR, insured status, PTV 11, the 116117 call, the
// Absagen, Konsiliarbericht, family doctor, earlier therapy — and the two
// choices of their treatment contract (Dok. 02 Ziffer 4/5). Returning patients
// (no insurance step this time) pick their insurance type and insurer here too.
// New private / self-paying patients skip this page (src/flow/flow.ts). No
// setting question: GAM only offers online sessions (Paul, 2026-10-29).
//
// Answer codes: server/src/services/vertraegeAnswers.ts — Justus's Make maps them verbatim.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Paperclip, Trash2, Loader2 } from 'lucide-react'
import { Input } from '../../components/Input'
import { Select } from '../../components/Select'
import { Textarea } from '../../components/Textarea'
import { Checkbox } from '../../components/Checkbox'
import { FormNavigation } from '../../components/FormNavigation'
import { InfoBox } from '../../components/InfoBox'
import { useOnboardingStore, type PtUpload } from '../../store/onboardingStore'
import { vertraegeService, getApiErrorCode } from '../../services/api'
import { GERMAN_PUBLIC_INSURERS, GERMAN_PRIVATE_INSURERS } from '../../data/insurers'
import { ptDetailsMissing, kvnrChecksumOk, kasseOf, KVNR_PATTERN, type PtUploadKind } from '../../flow/ptAnswers'
import { logger } from '../../utils/logger'
import type { StepProps } from '../../types'

type Choice = { value: string; label: string }

function ChoiceGroup({ name, label, value, options, onChange, required = true }: { name: string; label: string; value: unknown; options: Choice[]; onChange: (v: string) => void; required?: boolean }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-body font-medium text-foreground mb-1">{label}{required && <span className="text-destructive"> *</span>}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map(o => (
          <label key={o.value} className={`cursor-pointer rounded-input border px-3 py-2 text-small ${String(value) === o.value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border bg-white text-foreground'}`}>
            <input type="radio" name={name} value={o.value} checked={String(value) === o.value} onChange={() => onChange(o.value)} className="sr-only" />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export function PtDetailsStep({ onNext, onBack }: StepProps) {
  const { t } = useTranslation()
  const {
    cohort, ptAnswers: a, setPtAnswers, ptUploads, setPtUploads, insuranceData, setInsuranceData,
  } = useOnboardingStore()
  const returning = cohort === 'psychotherapy_returning'
  const insuranceType = insuranceData.insuranceType
  const isPublic = insuranceType === 'public'
  const isPrivate = insuranceType === 'private'
  const [uploading, setUploading] = useState<PtUploadKind | null>(null)
  const [kvnrWarned, setKvnrWarned] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const fileInputs = { ptv11: useRef<HTMLInputElement>(null), absageprotokoll: useRef<HTMLInputElement>(null) }

  const yesNo: Choice[] = [{ value: 'true', label: t('pt.details.yes') }, { value: 'false', label: t('pt.details.no') }]
  const setBool = (k: string) => (v: string) => setPtAnswers({ [k]: v === 'true' })
  const setText = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPtAnswers({ [k]: e.target.value })
  const setInt = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = e.target.value === '' ? undefined : Math.max(0, Math.floor(Number(e.target.value)))
    setPtAnswers({ [k]: Number.isFinite(n) ? n : undefined })
  }
  const str = (k: string) => (a[k] === undefined || a[k] === null ? '' : String(a[k]))

  const uploadCounts = useMemo(() => {
    const c: Partial<Record<PtUploadKind, number>> = {}
    for (const u of ptUploads) c[u.kind] = (c[u.kind] ?? 0) + 1
    return c
  }, [ptUploads])

  // Dok. 02 Ziffer 4: starting right away as a self-payer is the default for statutory insured
  // patients and preselected (Justus's note, D-32) — the patient can change it
  useEffect(() => {
    if (isPublic && a.prob_choice === undefined) setPtAnswers({ prob_choice: 'self_pay_now' })
  }, [isPublic, a.prob_choice, setPtAnswers])

  // The insurer is printed on Dok. 01 as the Kostenträger
  const kasseMissing = returning && (isPublic || isPrivate) && !kasseOf(insuranceData)
  const typeMissing = returning && !insuranceType
  const missing = ptDetailsMissing(a, insuranceType, uploadCounts)
  const valid = missing.length === 0 && !kasseMissing && !typeMissing
  const err = (k: string) => (showErrors && missing.includes(k) ? t('pt.details.required') : undefined)

  const upload = async (kind: PtUploadKind, file: File) => {
    setUploading(kind)
    try {
      const info = await vertraegeService.upload(kind, file)
      setPtUploads(prev => [...prev, info as PtUpload])
    } catch (e) {
      const code = getApiErrorCode(e)
      logger.error('[PT] upload failed:', e)
      toast.error(code === 'unsupported_file' || code === 'unsupported_image' ? t('pt.details.uploadType') : code === 'too_many_files' ? t('pt.details.uploadTooMany') : t('pt.details.uploadFailed'))
    } finally {
      setUploading(null)
    }
  }
  const removeUpload = async (u: PtUpload) => {
    try {
      await vertraegeService.removeUpload(u.id)
    } catch (e) {
      logger.warn('[PT] remove failed:', e)
    }
    setPtUploads(prev => prev.filter(x => x.id !== u.id))
  }

  const uploadBlock = (kind: PtUploadKind) => {
    const files = ptUploads.filter(u => u.kind === kind)
    return (
      <div className="space-y-2">
        <p className="text-small text-muted-foreground">{t(`pt.details.upload.${kind}`)}<span className="text-destructive"> *</span></p>
        {files.map(f => (
          <div key={f.id} className="flex items-center justify-between rounded-input border border-border bg-white px-3 py-2 text-small">
            <span className="flex items-center gap-2 truncate"><Paperclip className="w-4 h-4 flex-shrink-0" />{f.name || t('pt.details.file')}</span>
            <button type="button" onClick={() => removeUpload(f)} aria-label={t('pt.details.remove')} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <input ref={fileInputs[kind]} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) void upload(kind, f); e.target.value = '' }} />
        <button type="button" disabled={uploading !== null} onClick={() => fileInputs[kind].current?.click()}
          className="inline-flex items-center gap-2 rounded-input border-2 border-dashed border-primary/40 px-4 py-2 text-small text-primary">
          {uploading === kind ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          {files.length > 0 ? t('pt.details.addPage') : t('pt.details.chooseFile')}
        </button>
        {showErrors && missing.includes(`upload_${kind}`) && <p className="text-small text-destructive">{t('pt.details.uploadRequired')}</p>}
      </div>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) { setShowErrors(true); toast.error(t('pt.details.incomplete')); return }
    // A typo in the KVNR is caught by its check digit — said once, then the patient decides
    const nr = String(a.insurance_number ?? '')
    if (isPublic && nr && !kvnrChecksumOk(nr) && kvnrWarned !== nr) { setKvnrWarned(nr); return }
    onNext()
  }

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12 space-y-8">
        <div>
          <h2 className="text-h2 text-foreground mb-2">{t('pt.details.title')}</h2>
          <p className="text-body text-muted-foreground">{returning ? t('pt.details.introReturning') : t('pt.details.intro')}</p>
        </div>

        {returning && (
          <section className="space-y-4">
            <ChoiceGroup name="insurance_type" label={t('pt.details.insuranceType')} value={insuranceType}
              options={[{ value: 'public', label: t('pt.details.public') }, { value: 'private', label: t('pt.details.private') }, { value: 'none', label: t('pt.details.selfPayer') }]}
              onChange={v => setInsuranceData({ insuranceType: v as 'public' | 'private' | 'none', insuranceProvider: v === insuranceType ? insuranceData.insuranceProvider : '' })} />
            {(isPublic || isPrivate) && (
              <Select id="insurance_name" label={isPublic ? t('pt.details.kasse') : t('pt.details.privateInsurer')} value={insuranceData.insuranceProvider}
                onChange={e => setInsuranceData({ insuranceProvider: e.target.value, insuranceProviderOther: '' })}
                options={isPublic ? GERMAN_PUBLIC_INSURERS : GERMAN_PRIVATE_INSURERS}
                error={showErrors && kasseMissing && insuranceData.insuranceProvider !== 'Other' ? t('pt.details.required') : undefined} />
            )}
            {(isPublic || isPrivate) && insuranceData.insuranceProvider === 'Other' && (
              <Input id="insurance_name_other" label={t('pt.details.insurerOther')} value={insuranceData.insuranceProviderOther ?? ''}
                onChange={e => setInsuranceData({ insuranceProviderOther: e.target.value })}
                error={showErrors && kasseMissing ? t('pt.details.required') : undefined} />
            )}
          </section>
        )}

        {isPublic && (
          <>
            <section className="space-y-4">
              <h3 className="text-h3 text-foreground">{t('pt.details.insurance')}</h3>
              <Input id="insurance_number" label={`${t('pt.details.kvnr')} *`} placeholder="A123456789" value={str('insurance_number')} maxLength={10}
                onChange={e => setPtAnswers({ insurance_number: e.target.value.toUpperCase().replace(/\s/g, '') })}
                error={err('insurance_number') ?? (str('insurance_number') && !KVNR_PATTERN.test(str('insurance_number')) ? t('pt.details.kvnrFormat') : undefined)} />
              {kvnrWarned && kvnrWarned === str('insurance_number') && (
                <InfoBox><p>{t('pt.details.kvnrChecksum')}</p></InfoBox>
              )}
              <ChoiceGroup name="insured_status" label={t('pt.details.insuredStatus')} value={a.insured_status}
                options={[{ value: 'haupt', label: t('pt.details.haupt') }, { value: 'familienversichert', label: t('pt.details.familie') }]}
                onChange={v => setPtAnswers({ insured_status: v, ...(v === 'haupt' ? { main_insured_name: undefined } : {}) })} />
              {a.insured_status === 'familienversichert' && (
                <Input id="main_insured_name" label={`${t('pt.details.mainInsured')} *`} value={str('main_insured_name')} onChange={setText('main_insured_name')} error={err('main_insured_name')} />
              )}
            </section>

            <section className="space-y-4">
              <h3 className="text-h3 text-foreground">{t('pt.details.documents')}</h3>
              <p className="text-small text-muted-foreground">{t('pt.details.documentsHint')}</p>

              <ChoiceGroup name="has_ptv11" label={t('pt.details.hasPtv11')} value={a.has_ptv11} options={yesNo} onChange={setBool('has_ptv11')} />
              {a.has_ptv11 === true && (
                <div className="space-y-4 border-l-2 border-primary/20 pl-4">
                  {uploadBlock('ptv11')}
                  <Input id="ptv11_date" type="date" label={`${t('pt.details.ptv11Date')} *`} value={str('ptv11_date')} onChange={setText('ptv11_date')} error={err('ptv11_date')} />
                  <ChoiceGroup name="ptv11_urgent" label={t('pt.details.ptv11Urgent')} value={a.ptv11_urgent}
                    options={[{ value: 'ja', label: t('pt.details.yes') }, { value: 'nein', label: t('pt.details.no') }, { value: 'weiss_nicht', label: t('pt.details.dontKnow') }]}
                    onChange={v => setPtAnswers({ ptv11_urgent: v })} />
                </div>
              )}

              <ChoiceGroup name="tss_called" label={t('pt.details.tssCalled')} value={a.tss_called} options={yesNo} onChange={setBool('tss_called')} />
              {a.tss_called === true && (
                <div className="space-y-4 border-l-2 border-primary/20 pl-4">
                  <Input id="tss_date" type="date" label={`${t('pt.details.tssDate')} *`} value={str('tss_date')} onChange={setText('tss_date')} error={err('tss_date')} />
                  <ChoiceGroup name="tss_result" label={t('pt.details.tssResult')} value={a.tss_result}
                    options={[{ value: 'no_appointment', label: t('pt.details.tssNoAppointment') }, { value: 'consultation_only', label: t('pt.details.tssConsultationOnly') }, { value: 'waiting_over_x_weeks', label: t('pt.details.tssWaiting') }]}
                    onChange={v => setPtAnswers({ tss_result: v, ...(v !== 'waiting_over_x_weeks' ? { tss_wait_weeks: undefined } : {}) })} />
                  {a.tss_result === 'waiting_over_x_weeks' && (
                    <Input id="tss_wait_weeks" type="number" min={0} inputMode="numeric" label={`${t('pt.details.tssWeeks')} *`} value={str('tss_wait_weeks')} onChange={setInt('tss_wait_weeks')} error={err('tss_wait_weeks')} />
                  )}
                </div>
              )}

              <ChoiceGroup name="has_absagen" label={t('pt.details.hasAbsagen')} value={a.has_absagen} options={yesNo} onChange={setBool('has_absagen')} />
              {a.has_absagen === true && (
                <div className="space-y-4 border-l-2 border-primary/20 pl-4">
                  {uploadBlock('absageprotokoll')}
                  <Input id="absagen_count" type="number" min={1} inputMode="numeric" label={`${t('pt.details.absagenCount')} *`} value={str('absagen_count')} onChange={setInt('absagen_count')} error={err('absagen_count')} />
                  {typeof a.absagen_count === 'number' && a.absagen_count > 0 && a.absagen_count < 5 && (
                    <p className="text-small text-amber-700">{t('pt.details.absagenLow')}</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input id="absagen_from" type="date" label={`${t('pt.details.absagenFrom')} *`} value={str('absagen_from')} onChange={setText('absagen_from')} error={err('absagen_from')} />
                    <Input id="absagen_to" type="date" label={`${t('pt.details.absagenTo')} *`} value={str('absagen_to')} onChange={setText('absagen_to')} error={err('absagen_to')} />
                  </div>
                </div>
              )}

              <ChoiceGroup name="has_konsil" label={t('pt.details.hasKonsil')} value={a.has_konsil}
                options={[{ value: 'ja', label: t('pt.details.yes') }, { value: 'nein', label: t('pt.details.no') }, { value: 'termin_vereinbart', label: t('pt.details.konsilAppointment') }]}
                onChange={v => setPtAnswers({ has_konsil: v })} />
            </section>

            <section className="space-y-4">
              <h3 className="text-h3 text-foreground">{t('pt.details.gp')}</h3>
              <Input id="gp_name" label={`${t('pt.details.gpName')} *`} value={str('gp_name')} onChange={setText('gp_name')} error={err('gp_name')} />
              <Input id="gp_practice" label={`${t('pt.details.gpPractice')} *`} value={str('gp_practice')} onChange={setText('gp_practice')} error={err('gp_practice')} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input id="gp_city" label={`${t('pt.details.gpCity')} *`} value={str('gp_city')} onChange={setText('gp_city')} error={err('gp_city')} />
                <Input id="gp_fax" type="tel" label={t('pt.details.gpFax')} value={str('gp_fax')} onChange={setText('gp_fax')} />
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-h3 text-foreground">{t('pt.details.history')}</h3>
              <ChoiceGroup name="prior_therapy" label={t('pt.details.priorTherapy')} value={a.prior_therapy} options={yesNo}
                onChange={v => setPtAnswers({ prior_therapy: v === 'true', ...(v === 'false' ? { prior_therapy_details: undefined } : {}) })} />
              {a.prior_therapy === true && (
                <Textarea id="prior_therapy_details" label={`${t('pt.details.priorTherapyDetails')} *`} rows={3} value={str('prior_therapy_details')} onChange={setText('prior_therapy_details')} error={err('prior_therapy_details')} />
              )}
              <ChoiceGroup name="psychiatric_care" label={t('pt.details.psychiatricCare')} value={a.psychiatric_care} options={yesNo}
                onChange={v => setPtAnswers({ psychiatric_care: v === 'true', ...(v === 'false' ? { psychiatric_care_details: undefined } : {}) })} />
              {a.psychiatric_care === true && (
                <Textarea id="psychiatric_care_details" label={`${t('pt.details.psychiatricCareDetails')} *`} rows={2} value={str('psychiatric_care_details')} onChange={setText('psychiatric_care_details')} error={err('psychiatric_care_details')} />
              )}
            </section>

            <section className="space-y-4">
              <h3 className="text-h3 text-foreground">{t('pt.details.contract')}</h3>
              <p className="text-small text-muted-foreground">{t('pt.details.contractHint')}</p>
              <fieldset className="space-y-2">
                <legend className="text-body font-medium text-foreground mb-1">{t('pt.details.probChoice')}<span className="text-destructive"> *</span></legend>
                {(['self_pay_now', 'after_approval'] as const).map(v => (
                  <label key={v} className={`block cursor-pointer rounded-input border px-4 py-3 ${a.prob_choice === v ? 'border-primary bg-primary/5' : 'border-border bg-white'}`}>
                    <input type="radio" name="prob_choice" value={v} checked={a.prob_choice === v} onChange={() => setPtAnswers({ prob_choice: v })} className="sr-only" />
                    <span className={`block text-small ${a.prob_choice === v ? 'text-primary font-medium' : 'text-foreground font-medium'}`}>{t(`pt.details.prob.${v}`)}</span>
                    <span className="block text-small text-muted-foreground mt-1">{t(`pt.details.prob.${v}Body`)}</span>
                  </label>
                ))}
                {showErrors && missing.includes('prob_choice') && <p className="text-small text-destructive">{t('pt.details.required')}</p>}
              </fieldset>
              <Checkbox id="assignment" checked={a.assignment === true} onChange={e => setPtAnswers({ assignment: e.target.checked })}
                label={<span className="text-small">{t('pt.details.assignment')}</span>} />
            </section>
          </>
        )}

        <FormNavigation onBack={onBack} nextDisabled={uploading !== null} />
      </form>
    </div>
  )
}
